import { useEffect, useMemo, useState } from "react";
import { useShowSendToSamboKun } from "@/features/partner/hooks/useShowSendToSamboKun";
import { useSamboKunLimit } from "@/features/partner/hooks/useSamboKunLimit";
import { isSendToSamboKunDisabled } from "@/features/partner/utils/isSendDisabled";
import {
  calcExcludedDetailIds,
  countSelectedRowsWithDetailId,
  type RowWithDetailIds,
} from "@/features/partner/utils/calcExcludedDetailIds";

export type SamboKunDialogTarget = "owner" | "allMatters";

interface UseSamboKunFeedGridIntegrationParams {
  /**
   * 取得済不動産登記画面の全行（フィルタ非適用）。
   * mergeRowsByProperty 済みで RowWithDetailIds と構造的に互換である前提。
   */
  convertedRows: readonly RowWithDetailIds[];
  /** DataGrid の選択モデル（GridSelectionModel の値）。 */
  selectedRowIds: ReadonlyArray<number | string>;
  /** 紐づく所有者事項解析リクエスト ID。null/undefined ならボタン Disable。 */
  bindOwnerAnalyzeRequestId: number | null | undefined;
  /** 紐づく全部事項解析リクエスト ID。null/undefined ならボタン Disable。 */
  bindAllMattersAnalyzeRequestId: number | null | undefined;
}

interface UseSamboKunFeedGridIntegrationResult {
  /** 参謀くん UI を表示すべきか（feature flag / SSO 連携状態の AND）。 */
  showSendToSamboKun: boolean;
  /** 現在開かれているダイアログの対象。null なら閉じている。 */
  samboKunDialogTarget: SamboKunDialogTarget | null;
  /** ダイアログを開く。`showSendToSamboKun=true` のときのみ有効。 */
  openSamboKunDialog: (target: SamboKunDialogTarget) => void;
  /** ダイアログを閉じる。 */
  closeSamboKunDialog: () => void;
  /** 選択行を除外した結果、参謀くんに連携しない picture_request_detail.id の配列。 */
  excludedPictureRequestDetailIds: number[];
  /** 選択行のうち所有者事項として有効な行の件数。 */
  selectedOwnerCount: number;
  /** 選択行のうち全部事項として有効な行の件数。 */
  selectedAllMattersCount: number;
  /** 所有者事項連携ボタンの Disable 判定。 */
  isSamboKunOwnerDisabled: boolean;
  /** 全部事項連携ボタンの Disable 判定。 */
  isSamboKunAllMattersDisabled: boolean;
  /**
   * 連携ボタンのツールチップ文言を返す。
   * 0 件選択 / リミット取得失敗 / リミットロード中 / 上限超過 を hook 内で判定し、
   * 対応する文言を返す（呼び出し側が状態を渡す必要なし）。
   * @param target - "owner" / "allMatters"
   */
  getSamboKunTooltipText: (target: SamboKunDialogTarget) => string;
}

/**
 * 取得済不動産登記画面の参謀くん連携 UI まわりの状態管理と派生値を集約した hook。
 *
 * FeedGrid 本体は MUI DataGrid Pro 等の重い依存を抱えるため、選択操作と
 * Dialog の wiring に関わるロジックをこの hook に切り出してテスト可能にする。
 *
 * @see useShowSendToSamboKun - 連携 UI の表示フラグ
 * @see useSamboKunLimit - 連携上限件数の取得（hook 内部で表示時のみ有効化）
 * @see calcExcludedDetailIds - 除外明細 ID の計算
 * @see countSelectedRowsWithDetailId - 種別ごとの選択件数計算
 * @see isSendToSamboKunDisabled - 上限超過判定
 */
export const useSamboKunFeedGridIntegration = (
  params: UseSamboKunFeedGridIntegrationParams
): UseSamboKunFeedGridIntegrationResult => {
  const {
    convertedRows,
    selectedRowIds,
    bindOwnerAnalyzeRequestId,
    bindAllMattersAnalyzeRequestId,
  } = params;

  const showSendToSamboKun = useShowSendToSamboKun();
  // 両 bind ID が null の場合は連携ボタンを描画しないため、リミット API を
  // 叩く意味がない。feature flag に加えて bind ID 存在も enabled の条件に
  // 含めて、UI 非表示時の polling を完全に抑止する。
  const hasAnyBindRequestId =
    bindOwnerAnalyzeRequestId != null || bindAllMattersAnalyzeRequestId != null;
  const { maxPropertiesPerRequest, isError: isLimitError } = useSamboKunLimit({
    enabled: showSendToSamboKun && hasAnyBindRequestId,
  });
  const [samboKunDialogTarget, setSamboKunDialogTarget] =
    useState<SamboKunDialogTarget | null>(null);

  // LaunchDarkly フラグや SSO 連携状態がランタイムで OFF に変わった場合、
  // 開きっぱなしのダイアログから送信できてしまわないよう state を強制クリアする
  useEffect(() => {
    if (!showSendToSamboKun) {
      setSamboKunDialogTarget(null);
    }
  }, [showSendToSamboKun]);

  // 選択行のうち、種別ごとに実際に明細 ID を持ち取得成功している行の件数。
  // 図面のみ・取得失敗・他ユーザー取得の行は分母に含めない（util 内で除外）。
  const selectedOwnerCount = useMemo(
    () => countSelectedRowsWithDetailId(convertedRows, selectedRowIds, "owner"),
    [convertedRows, selectedRowIds]
  );
  const selectedAllMattersCount = useMemo(
    () =>
      countSelectedRowsWithDetailId(
        convertedRows,
        selectedRowIds,
        "allMatters"
      ),
    [convertedRows, selectedRowIds]
  );

  const excludedPictureRequestDetailIds = useMemo(
    () => calcExcludedDetailIds(convertedRows, selectedRowIds),
    [convertedRows, selectedRowIds]
  );

  const isSamboKunOwnerDisabled =
    bindOwnerAnalyzeRequestId == null ||
    selectedOwnerCount === 0 ||
    isSendToSamboKunDisabled(
      selectedOwnerCount,
      maxPropertiesPerRequest,
      isLimitError
    );

  const isSamboKunAllMattersDisabled =
    bindAllMattersAnalyzeRequestId == null ||
    selectedAllMattersCount === 0 ||
    isSendToSamboKunDisabled(
      selectedAllMattersCount,
      maxPropertiesPerRequest,
      isLimitError
    );

  const getSamboKunTooltipText = (target: SamboKunDialogTarget): string => {
    const effectiveCount =
      target === "owner" ? selectedOwnerCount : selectedAllMattersCount;
    if (effectiveCount === 0) {
      return target === "owner"
        ? "所有者事項が取得済みの行を 1 件以上選択してください"
        : "全部事項が取得済みの行を 1 件以上選択してください";
    }
    if (isLimitError) {
      return "連携上限件数を取得できませんでした。しばらく待ってから再試行してください";
    }
    if (maxPropertiesPerRequest == null) {
      // ロード中はユーザーに誤った「取得失敗」表示を出さず、ボタン Disable のみで待たせる
      return "";
    }
    if (effectiveCount > maxPropertiesPerRequest) {
      return "連携可能な物件数の上限を超えています";
    }
    return "";
  };

  const openSamboKunDialog = (target: SamboKunDialogTarget): void => {
    setSamboKunDialogTarget(target);
  };

  const closeSamboKunDialog = (): void => {
    setSamboKunDialogTarget(null);
  };

  return {
    showSendToSamboKun,
    samboKunDialogTarget,
    openSamboKunDialog,
    closeSamboKunDialog,
    excludedPictureRequestDetailIds,
    selectedOwnerCount,
    selectedAllMattersCount,
    isSamboKunOwnerDisabled,
    isSamboKunAllMattersDisabled,
    getSamboKunTooltipText,
  };
};
